import { BaseApiService } from '@/shared/api/services/base.service';
import { buildUrl } from '@/shared/lib/query-params';
import type {
  PlanResource,
  PlansResponse,
  PlanResponse,
  SubscriptionResource,
  SubscriptionsResponse,
  SubscriptionResponse,
  CheckoutSessionRequest,
  CheckoutSessionResponse,
} from '@/types/subscription';

/**
 * In-flight dedupe map for `checkCheckoutStatus`.
 *
 * The checkout success page polls every 3 seconds and is also reachable from
 * multiple tabs (user clicks "open in new tab" from email, or refreshes after
 * a Stripe/MAIB redirect). Without deduping, each tab/poll fires its own HTTP
 * request, and a slow backend reply from tab A can race-clobber tab B's loading
 * state. Sharing the promise means concurrent callers all await the same
 * resolution and the server sees one request per distinct payment_id.
 */
const checkoutStatusInFlight = new Map<
  string,
  Promise<{
    has_active_subscription: boolean;
    subscription_id?: string;
    plan_name?: string;
    status?: string;
    activated_at?: string;
    payment_processing?: boolean;
    message?: string;
  }>
>();

class SubscriptionService extends BaseApiService {
  async getPublicPlans(): Promise<PlanResource[]> {
    const response = await this.httpGet<PlansResponse>('/v1/plans/public');
    return response.data;
  }

  async getPlans(params?: {
    sort?: string;
    filter?: Record<string, string | number | boolean>;
  }): Promise<PlanResource[]> {
    const url = buildUrl('/v1/plans', {
      sort: params?.sort,
      filter: params?.filter,
    });
    const response = await this.httpGet<PlansResponse>(url);
    return response.data;
  }

  async getTrialPlans(params?: {
    sort?: string;
    filter?: Record<string, string | number | boolean>;
  }): Promise<PlanResource[]> {
    const trialFilter = {
      is_trial: true,
      is_active: true,
      is_public: true,
      ...params?.filter,
    };
    return this.getPlans({
      sort: params?.sort || 'price',
      filter: trialFilter,
    });
  }

  async getPlan(planId: string): Promise<PlanResource> {
    const response = await this.httpGet<PlanResponse>(`/v1/plans/${planId}`);
    return response.data;
  }

  async getSubscriptions(): Promise<SubscriptionResource[]> {
    const response = await this.httpGet<SubscriptionsResponse>('/v1/subscriptions');
    return response.data;
  }

  async getSubscription(subscriptionId: string): Promise<SubscriptionResource> {
    const response = await this.httpGet<SubscriptionResponse>(
      `/v1/subscriptions/${subscriptionId}`
    );
    return response.data;
  }

  // NO RETRY for checkout operations — dangerous for payment flows
  async createCheckoutSession(request: CheckoutSessionRequest): Promise<{
    checkoutUrl?: string | null;
    instant_payment?: boolean;
    status?: string;
    subscription_id?: string;
  }> {
    // Add gateway parameter for development — use MAIB by default
    const requestWithGateway = {
      ...request,
      gateway: 'maib',
      success_url: `${window.location.origin}/checkout/success`,
      cancel_url: `${window.location.origin}/checkout/failure`,
    };

    const response = await this.post<CheckoutSessionResponse>(
      '/v1/subscriptions/checkout-session',
      this.formatJsonApiData('checkout_session', requestWithGateway)
    );

    return {
      checkoutUrl: response.data.attributes.checkout_url,
      instant_payment: response.data.attributes.instant_payment,
      status: response.data.attributes.status,
      subscription_id: response.data.attributes.subscription_id,
    };
  }

  async cancelSubscription(
    subscriptionId: string,
    reason?: string
  ): Promise<void> {
    const options = reason
      ? {
          body: JSON.stringify({
            data: {
              type: 'subscription',
              id: subscriptionId,
              attributes: {
                cancel_reason: reason,
              },
            },
          }),
        }
      : undefined;

    await this.httpDelete(`/v1/subscriptions/${subscriptionId}`, options);
  }

  // NO RETRY for payment operations
  async createDirectSubscription(
    planId: string,
    promoCode?: string,
    billingPeriod?: { id: string; type: string }
  ): Promise<SubscriptionResource> {
    const response = await this.post<SubscriptionResponse>(
      '/v1/subscriptions',
      this.formatJsonApiData('subscription', {
        plan_id: planId,
        promo_code: promoCode || null,
        billing_period: billingPeriod || { id: planId, type: 'resource' },
      })
    );
    return response.data;
  }

  async createPlanChangeSession(newPlanId: string): Promise<{
    checkoutUrl?: string | null;
    instant_payment?: boolean;
    status?: string;
    subscription_id?: string;
  }> {
    const request: CheckoutSessionRequest = {
      plan_id: newPlanId,
    };
    return this.createCheckoutSession(request);
  }

  // Concurrent calls for the same payment_id share a single in-flight request —
  // see the `checkoutStatusInFlight` doc above for the full reasoning.
  async checkCheckoutStatus(paymentId: string): Promise<{
    has_active_subscription: boolean;
    subscription_id?: string;
    plan_name?: string;
    status?: string;
    activated_at?: string;
    payment_processing?: boolean;
    message?: string;
  }> {
    const existing = checkoutStatusInFlight.get(paymentId);
    if (existing) {
      return existing;
    }

    const url = `/v1/subscriptions/checkout-status?payment_id=${encodeURIComponent(paymentId)}`;

    const promise = this.httpGet<{
      data: {
        type: string;
        attributes: {
          has_active_subscription: boolean;
          subscription_id?: string;
          plan_name?: string;
          status?: string;
          activated_at?: string;
          payment_processing?: boolean;
          message?: string;
        };
      };
    }>(url)
      .then((response) => response.data.attributes)
      .finally(() => {
        // Always clear — the next poll tick should hit the network to observe
        // backend state changes (e.g. "processing" → "active"), not get a
        // cached "processing" from the previous call.
        checkoutStatusInFlight.delete(paymentId);
      });

    checkoutStatusInFlight.set(paymentId, promise);
    return promise;
  }

  async previewPlanChange(
    subscriptionId: string,
    planId: string
  ): Promise<{
    immediate_charge?: number;
    next_billing_date?: string;
    next_billing_amount?: number;
    proration_amount?: number;
    change_type?: 'upgrade' | 'downgrade';
    effective_date?: string;
    gains?: string[];
    losses?: string[];
  }> {
    const response = await this.httpGet<{
      data: {
        type: string;
        attributes: {
          immediate_charge?: number;
          next_billing_date?: string;
          next_billing_amount?: number;
          proration_amount?: number;
          change_type?: 'upgrade' | 'downgrade';
          effective_date?: string;
          gains?: string[];
          losses?: string[];
        };
      };
    }>(
      `/v1/subscriptions/${subscriptionId}/preview-change?plan_id=${planId}`
    );
    return response.data.attributes;
  }

  async comparePlans(
    fromPlanId: string,
    toPlanId: string
  ): Promise<{ gains: string[]; losses: string[] }> {
    const response = await this.httpGet<{
      data: {
        type: string;
        attributes: {
          from_plan: string;
          to_plan: string;
          gains: string[];
          losses: string[];
        };
      };
    }>(`/v1/plans/${fromPlanId}/compare/${toPlanId}`);
    return response.data.attributes;
  }

  async changePlan(
    subscriptionId: string,
    planId: string
  ): Promise<SubscriptionResource> {
    const response = await this.post<SubscriptionResponse>(
      `/v1/subscriptions/${subscriptionId}/change-plan`,
      this.formatJsonApiData('subscription-change', { plan_id: planId })
    );
    return response.data;
  }

  async createUpgradeCheckoutSession(
    subscriptionId: string,
    planId: string
  ): Promise<{
    checkoutUrl?: string | null;
    instant_payment?: boolean;
    status?: string;
    subscription_id?: string;
  }> {
    const response = await this.post<CheckoutSessionResponse>(
      `/v1/subscriptions/${subscriptionId}/upgrade-checkout`,
      this.formatJsonApiData('upgrade-checkout', { plan_id: planId })
    );
    return {
      checkoutUrl: response.data.attributes.checkout_url,
      instant_payment: response.data.attributes.instant_payment,
      status: response.data.attributes.status,
      subscription_id: response.data.attributes.subscription_id,
    };
  }

  async upgradeWithSavedCard(
    subscriptionId: string,
    planId: string,
    paymentMethodId: string
  ): Promise<SubscriptionResource> {
    const response = await this.post<SubscriptionResponse>(
      `/v1/subscriptions/${subscriptionId}/change-plan`,
      this.formatJsonApiData('subscription-change', {
        plan_id: planId,
        payment_method_id: paymentMethodId,
      })
    );
    return response.data;
  }

  async cancelScheduledChange(subscriptionId: string): Promise<void> {
    await this.httpDelete(`/v1/subscriptions/${subscriptionId}/scheduled-change`);
  }

  async resume(subscriptionId: string): Promise<SubscriptionResource> {
    const response = await this.post<SubscriptionResponse>(
      `/v1/subscriptions/${subscriptionId}/resume`,
      this.formatJsonApiData('subscription', {}, undefined, subscriptionId)
    );
    return response.data;
  }
}

export const subscriptionService = new SubscriptionService();
